import {
  Alert,
  AloftArea,
  AloftDate,
  AloftPrice,
  Improvements,
  LocationType,
  ManualAdjustmentKey,
  ORDER_FEATURES,
  SiteRating,
  SixTierQuality,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schLocationType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import {
  getAduMaterialityParams,
  getBasementMaterialityParams,
  getGarageCarportMaterialityParams,
  isAduAdjustmentMaterial,
  isBasementAdjustmentMaterial,
  isBathAdjustmentMaterial,
  isConditionAdjustmentMaterial,
  isDateOfSaleAdjustmentMaterial,
  isGarageCarportAdjustmentMaterial,
  isGlaAdjustmentMaterial,
  isQualityAdjustmentMaterial,
  isSiteInfluenceAdjustmentMaterial,
  isSiteSizeAdjustmentMaterial,
  isViewAdjustmentMaterial,
} from '../utils/compAdjustmentMateriality.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

type SubjectData = SubjectPropertyDataState['subjectData']['editableData'];
type CompData = CompPropertyDataState['compData']['editableData'];
type BathCounts = { fullBaths?: number; halfBaths?: number };

const TARGET_CELL_TOOLTIP = 'Unexpected adjustment';

// Adjustment magnitude above which an adjustment with no material difference is flagged. Rating-level
// features flag any non-zero adjustment; GLA flags above $5k; everything else above $10k.
const RATING_DOLLAR_THRESHOLD = AloftPrice.zero;
const GLA_DOLLAR_THRESHOLD = AloftPrice.fromDollarLike(5000);
const DEFAULT_DOLLAR_THRESHOLD = AloftPrice.fromDollarLike(10000);

type MaterialityContext = { effectiveDate: AloftDate };

/**
 * Quantitative / qualitative fields
 *
 * Fires when an adjustment exceeds the feature's dollar threshold yet the Subject and Comp are not
 * materially different for that feature. This is the inverse of the missingCompAdjustment materiality
 * check, and reuses the same shared materiality helpers.
 */
type MaterialityCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  dollarThreshold: AloftPrice;
  getSubjectValue: (subject: SubjectData, ctx: MaterialityContext) => unknown;
  getCompValue: (comp: CompData) => unknown;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
  isMaterialDifference: (params: {
    subjectValue: unknown;
    compValue: unknown;
    locationType?: LocationType;
  }) => boolean;
};

const MATERIALITY_CHECKS: MaterialityCheck[] = [
  {
    label: 'Date of Sale',
    cellKey: 'dateOfSale',
    dollarThreshold: RATING_DOLLAR_THRESHOLD,
    getSubjectValue: (_s, ctx) => ctx.effectiveDate,
    getCompValue: (c) => {
      const month = c.transaction?.saleDate;
      return month ? AloftDate.fromIsoString(`${month}-01`) : undefined;
    },
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.dateOfSale?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isDateOfSaleAdjustmentMaterial(subjectValue as AloftDate, compValue as AloftDate),
  },
  {
    label: 'Site Size',
    cellKey: 'site',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => orUndefined(schAloftArea.parse)(s.site?.area),
    getCompValue: (c) => orUndefined(schAloftArea.parse)(c.site?.area),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.site?.size?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue, locationType }) =>
      isSiteSizeAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea, locationType),
  },
  {
    label: 'Site Influence',
    cellKey: 'siteInfluence',
    dollarThreshold: RATING_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.site?.influenceRating,
    getCompValue: (c) => c.site?.influenceRating,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.siteInfluence?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isSiteInfluenceAdjustmentMaterial(subjectValue as SiteRating, compValue as SiteRating),
  },
  {
    label: 'View',
    cellKey: 'siteView',
    dollarThreshold: RATING_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.site?.viewRating,
    getCompValue: (c) => c.site?.viewRating,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.siteView?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isViewAdjustmentMaterial(subjectValue as SiteRating, compValue as SiteRating),
  },
  {
    label: 'Quality',
    cellKey: 'quality',
    dollarThreshold: RATING_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.uadQuality,
    getCompValue: (c) => c.improvements?.uadQuality,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.quality?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isQualityAdjustmentMaterial(subjectValue as SixTierQuality, compValue as SixTierQuality),
  },
  {
    label: 'Condition',
    cellKey: 'condition',
    dollarThreshold: RATING_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.uadCondition,
    getCompValue: (c) => c.improvements?.uadCondition,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.condition?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isConditionAdjustmentMaterial(subjectValue as SixTierQuality, compValue as SixTierQuality),
  },
  {
    label: 'GLA',
    cellKey: 'gla',
    dollarThreshold: GLA_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => orUndefined(schAloftArea.parse)(s.improvements?.gla),
    getCompValue: (c) => orUndefined(schAloftArea.parse)(c.improvements?.gla),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.mainDwelling?.gla?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) =>
      isGlaAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea),
  },
  {
    label: 'Baths',
    cellKey: 'bathAboveGrade',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.bathAboveGrade?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = subjectValue as BathCounts;
      const c = compValue as BathCounts;
      return isBathAdjustmentMaterial(s.fullBaths, s.halfBaths, c.fullBaths, c.halfBaths);
    },
  },
  {
    label: 'Garage/Carport',
    cellKey: 'garageCarport',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.garageCarport?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = getGarageCarportMaterialityParams(subjectValue as Improvements);
      const c = getGarageCarportMaterialityParams(compValue as Improvements);
      return isGarageCarportAdjustmentMaterial({
        subjectHasFeature: s.hasFeature,
        compHasFeature: c.hasFeature,
        subjectSpaces: s.spaces,
        compSpaces: c.spaces,
        subjectParkingType: s.parkingSubtype,
        compParkingType: c.parkingSubtype,
      });
    },
  },
  {
    label: 'Basement',
    cellKey: 'basement',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.basement?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = getBasementMaterialityParams(subjectValue as Improvements);
      const c = getBasementMaterialityParams(compValue as Improvements);
      return isBasementAdjustmentMaterial({
        subjectHasFeature: s.hasFeature,
        compHasFeature: c.hasFeature,
        subjectTotalArea: s.totalArea,
        compTotalArea: c.totalArea,
        subjectFinishedArea: s.finishedArea,
        compFinishedArea: c.finishedArea,
      });
    },
  },
  {
    label: 'Basement Baths',
    cellKey: 'bathBelowGrade',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.basement,
    getCompValue: (c) => c.improvements?.basement,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.bathBelowGrade?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = subjectValue as BathCounts;
      const c = compValue as BathCounts;
      return isBathAdjustmentMaterial(s.fullBaths, s.halfBaths, c.fullBaths, c.halfBaths);
    },
  },
  {
    label: 'ADU',
    cellKey: 'adu',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements,
    getCompValue: (c) => c.improvements,
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.adu?.adjustment),
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = getAduMaterialityParams(subjectValue as Improvements);
      const c = getAduMaterialityParams(compValue as Improvements);
      return isAduAdjustmentMaterial({
        subjectHasFeature: s.hasFeature,
        compHasFeature: c.hasFeature,
        subjectArea: s.area,
        compArea: c.area,
      });
    },
  },
  {
    label: 'ADU Baths',
    cellKey: 'aduFullBaths',
    dollarThreshold: DEFAULT_DOLLAR_THRESHOLD,
    getSubjectValue: (s) => s.improvements?.adu,
    getCompValue: (c) => c.improvements?.adu,
    getAdjustment: (ca) =>
      AloftPrice.addWithNulls(
        orUndefined(schAloftPrice.parse)(ca.aduFullBaths?.adjustment),
        orUndefined(schAloftPrice.parse)(ca.aduHalfBaths?.adjustment),
      ) ?? undefined,
    isMaterialDifference: ({ subjectValue, compValue }) => {
      const s = subjectValue as BathCounts;
      const c = compValue as BathCounts;
      return isBathAdjustmentMaterial(s.fullBaths, s.halfBaths, c.fullBaths, c.halfBaths);
    },
  },
];

type PresenceCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  subjectHasFeature: (subject: SubjectData) => boolean;
  compHasFeature: (comp: CompData) => boolean;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
};

/**
 * Binary presence / absence fields
 *
 * Fires when a feature is present on BOTH properties (or absent on both), yet a non-zero
 * adjustment is recorded. This is the inverse of the missingCompAdjustment presence check.
 */
const PRESENCE_CHECKS: PresenceCheck[] = [
  {
    label: 'Pool/Spa',
    cellKey: 'poolSpa',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.pool.hasFeature(s.improvements) ||
      ORDER_FEATURES.spa.hasFeature(s.improvements),
    compHasFeature: (c) =>
      ORDER_FEATURES.pool.hasFeature(c.improvements) ||
      ORDER_FEATURES.spa.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.pool?.adjustment),
  },
  {
    label: 'Attic',
    cellKey: 'attic',
    subjectHasFeature: (s) => ORDER_FEATURES.attic.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.attic.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.attic?.adjustment),
  },
  {
    label: 'Fireplace',
    cellKey: 'fireplace',
    subjectHasFeature: (s) => ORDER_FEATURES.fireplace.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.fireplace.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.fireplace?.adjustment),
  },
  {
    label: 'Barn',
    cellKey: 'barn',
    subjectHasFeature: (s) => ORDER_FEATURES.barn.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.barn.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.barn?.adjustment),
  },
  {
    label: 'Shed',
    cellKey: 'shed',
    subjectHasFeature: (s) => ORDER_FEATURES.shed.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.shed.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.shed?.adjustment),
  },
  {
    label: 'Workshop',
    cellKey: 'workshop',
    subjectHasFeature: (s) => ORDER_FEATURES.workshop.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.workshop.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.workshop?.adjustment),
  },
  {
    label: 'Porch/Patio/Deck',
    cellKey: 'porchPatioDeck',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.porch.hasFeature(s.improvements) ||
      ORDER_FEATURES.patio.hasFeature(s.improvements) ||
      ORDER_FEATURES.deck.hasFeature(s.improvements),
    compHasFeature: (c) =>
      ORDER_FEATURES.porch.hasFeature(c.improvements) ||
      ORDER_FEATURES.patio.hasFeature(c.improvements) ||
      ORDER_FEATURES.deck.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.porchPatioDeck?.adjustment),
  },
  {
    label: 'Manual Cost',
    cellKey: 'manual',
    subjectHasFeature: (s) => ORDER_FEATURES.manualCost.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.manualCost.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.manual?.adjustment),
  },
];

export const unexpectedCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  order,
  propertyDataEntities,
  effectiveDate,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);

  const locationType = orUndefined(schLocationType.parse)(
    subjectData.propertyIdentification?.locationType,
  );

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order, includeUpdate: true });

  return compSequence.flatMap((id) => {
    const compData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
      .editableData;
    const compAdjustmentData = compAdjustments[id];
    if (!compData || !compAdjustmentData) return [];

    const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);

    const baseAlert = (cellKey: ManualAdjustmentKey, message: string, tips: string): Alert => ({
      type: getScopedAlertType(schAlertType.enum.unexpectedCompAdjustment, compType),
      status: 'active' as const,
      severity: 'error' as const,
      orderType: ['saas', 'appraisify'] as const,
      message,
      propertyID: id,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips,
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'] as const,
        commentAlwaysRequired: false,
        targetCellKey: `${id}-${cellKey}` as PropertyGridCellKey,
        targetCellTooltip: TARGET_CELL_TOOLTIP,
      },
      triggers,
    });

    const materialityAlerts = MATERIALITY_CHECKS.flatMap((check) => {
      const subjectValue = check.getSubjectValue(scopedSubjectData, { effectiveDate });
      const compValue = check.getCompValue(compData);
      if (!isDefined(subjectValue) || !isDefined(compValue)) return [];

      const adjustment = check.getAdjustment(compAdjustmentData);
      if (!isDefined(adjustment) || !adjustment.abs().gt(check.dollarThreshold)) return [];

      if (check.isMaterialDifference({ subjectValue, compValue, locationType })) return [];

      const { label, cellKey } = check;
      return [
        baseAlert(
          cellKey,
          `${propertyName} has a ${label} adjustment, but there is no material ${label} difference from the Subject.`,
          `Since the ${label} of ${propertyName} is not materially different from the Subject, no adjustment is expected. Remove the adjustment, or explain why it is justified.`,
        ),
      ];
    });

    const presenceAlerts = PRESENCE_CHECKS.flatMap((check) => {
      const subjectHas = check.subjectHasFeature(scopedSubjectData);
      const compHas = check.compHasFeature(compData);
      if (subjectHas !== compHas) return [];

      const adjustment = check.getAdjustment(compAdjustmentData);
      if (!isDefined(adjustment) || adjustment.equals(AloftPrice.zero)) return [];

      const { label, cellKey } = check;
      const bothVerb = subjectHas ? 'have' : 'do not have';
      return [
        baseAlert(
          cellKey,
          `${propertyName} has a ${label} adjustment, but both the Subject and ${propertyName} ${bothVerb} a ${label}.`,
          `Since both the Subject and ${propertyName} ${bothVerb} a ${label}, no adjustment is expected for this feature. Remove the adjustment, or explain why it is justified.`,
        ),
      ];
    });

    return [...materialityAlerts, ...presenceAlerts];
  });
};
